import { useNavigate } from "react-router-dom";
import {
  ArrowRight,
  Satellite,
  Radio,
  Zap,
  CheckCircle2,
  Sparkles,
  Info,
  CloudRain,
} from "lucide-react";
import { useLang } from "../context/LanguageContext";

// VSAT band decision helper: C-band vs Ku-band vs Ka-band. The choice has
// large implications for cost, antenna size, weather tolerance and use case
// — especially for Angola's tropical climate.

const COPY = {
  pt: {
    overline: "Qual banda VSAT escolher?",
    title: "Banda C, Ku ou Ka — qual é a certa para a sua operação?",
    subtitle:
      "A escolha da banda muda a antena, o custo, a resistência à chuva e a velocidade. Para Angola, com clima tropical e chuvas fortes, esta decisão é crítica. Veja qual banda encaixa no seu cenário.",
    labels: {
      best_for: "Ideal para",
      pros: "Vantagens",
      cons: "Limitações",
      tech: "Tecnologia",
      antenna: "Antena típica",
      rain: "Tolerância à chuva",
      cta: "Pedir orçamento VSAT",
      pick: "Mais usada em Angola",
    },
    rain_levels: {
      excellent: "Excelente — resistente a chuvas tropicais",
      moderate: "Moderada — perdas em chuva muito forte",
      sensitive: "Sensível — necessita link budget e ACM",
    },
    cta_help: {
      title: "Tem um site remoto sem fibra?",
      body: "Projetamos, instalamos e ativamos VSATs em qualquer ponto de Angola — desde plataformas offshore até operações mineiras no interior. Acordo de SLA e suporte local.",
      btn: "Falar com um especialista VSAT",
    },
  },
  en: {
    overline: "Which VSAT band should you pick?",
    title: "C-band, Ku-band or Ka-band — which is right for your operation?",
    subtitle:
      "The band choice impacts antenna size, cost, rain resilience and throughput. For Angola's tropical climate and heavy rains, this is a critical decision. See which band fits your scenario.",
    labels: {
      best_for: "Best for",
      pros: "Strengths",
      cons: "Trade-offs",
      tech: "Technology",
      antenna: "Typical antenna",
      rain: "Rain tolerance",
      cta: "Request VSAT quote",
      pick: "Most used in Angola",
    },
    rain_levels: {
      excellent: "Excellent — resists tropical rain",
      moderate: "Moderate — losses in very heavy rain",
      sensitive: "Sensitive — needs link budget and ACM",
    },
    cta_help: {
      title: "Got a remote site with no fiber?",
      body: "We design, install and activate VSATs anywhere in Angola — from offshore platforms to inland mining ops. SLA agreement and local support.",
      btn: "Talk to a VSAT specialist",
    },
  },
  es: {
    overline: "¿Qué banda VSAT elegir?",
    title: "Banda C, Ku o Ka — ¿cuál es la adecuada para tu operación?",
    subtitle:
      "La elección de banda cambia la antena, el coste, la resistencia a la lluvia y la velocidad. Para el clima tropical de Angola, es una decisión crítica. Mira qué banda encaja en tu escenario.",
    labels: {
      best_for: "Ideal para",
      pros: "Ventajas",
      cons: "Limitaciones",
      tech: "Tecnología",
      antenna: "Antena típica",
      rain: "Tolerancia a la lluvia",
      cta: "Solicitar presupuesto VSAT",
      pick: "Más usada en Angola",
    },
    rain_levels: {
      excellent: "Excelente — resistente a lluvias tropicales",
      moderate: "Moderada — pérdidas en lluvia muy fuerte",
      sensitive: "Sensible — requiere link budget y ACM",
    },
    cta_help: {
      title: "¿Tienes un sitio remoto sin fibra?",
      body: "Diseñamos, instalamos y activamos VSAT en cualquier punto de Angola — desde plataformas offshore hasta operaciones mineras del interior. SLA y soporte local.",
      btn: "Hablar con especialista VSAT",
    },
  },
};

const BANDS = {
  pt: [
    {
      id: "c-band",
      icon: "satellite",
      name: "Banda C",
      subtitle: "4–8 GHz · Long-haul robusta",
      featured: true,
      best_for:
        "Petróleo offshore, broadcast TV, backhaul de telecom e operações governamentais críticas em Angola.",
      tech: "Frequências 3.7–4.2 GHz (downlink) e 5.9–6.4 GHz (uplink). Tecnologia madura (anos 70), satélites GEO. Throughput moderado (Mbps), latência ~600 ms.",
      antenna: "1,8 m a 3,7 m (rural até 4,5 m)",
      rain: "excellent",
      pros: [
        "Praticamente imune a chuva forte (clima tropical)",
        "Cobertura wide-beam — alta disponibilidade",
        "Tecnologia estável e bem suportada",
        "Ideal para Angola por causa das chuvas",
      ],
      cons: [
        "Antenas grandes (1,8–3,7 m) — necessitam fundação",
        "Equipamento e modems mais caros",
        "Throughput menor que Ku/Ka",
      ],
      produto: "VSAT Banda C",
    },
    {
      id: "ku-band",
      icon: "radio",
      name: "Banda Ku",
      subtitle: "12–18 GHz · Equilíbrio custo/desempenho",
      featured: false,
      best_for:
        "Empresas, ONGs, escritórios em províncias, sites mineiros e operações com necessidade de bom débito a custo médio.",
      tech: "Frequências 11.7–12.2 GHz (downlink) e 14–14.5 GHz (uplink). Throughput de 2 a 50+ Mbps, latência ~600 ms. Padrão da indústria.",
      antenna: "0,74 m a 1,8 m",
      rain: "moderate",
      pros: [
        "Antenas compactas — fácil instalação",
        "Bom equilíbrio custo/débito",
        "Vasto ecossistema de hubs e modems iDirect/Newtec",
        "Aceleração TCP e VPN nativa",
      ],
      cons: [
        "Atenuação por chuva forte (rain fade ocasional)",
        "Requer link budget cuidadoso na costa angolana",
        "Throughput limitado por contention ratio",
      ],
      produto: "VSAT Banda Ku",
    },
    {
      id: "ka-band",
      icon: "zap",
      name: "Banda Ka",
      subtitle: "18–40 GHz · HTS de alta velocidade",
      featured: false,
      best_for:
        "Internet de alta velocidade em sites pequenos, SOHO, IoT, conectividade móvel e backup de fibra.",
      tech: "Frequências 18.3–20.2 GHz (downlink) e 27.5–30 GHz (uplink). Satélites HTS (High Throughput) com spot beams — débitos até 100+ Mbps por terminal.",
      antenna: "0,74 m a 1,2 m (compacta)",
      rain: "sensitive",
      pros: [
        "Maior débito disponível (até 100+ Mbps)",
        "Custo Mbit/€ mais baixo",
        "Antenas pequenas e leves",
        "Ideal para internet rápida em SOHO",
      ],
      cons: [
        "Muito sensível a chuva tropical (rain fade forte)",
        "Latência GEO igual a Ku (~600 ms)",
        "Cobertura limitada a footprint do satélite HTS",
        "Menos adequada para missões críticas",
      ],
      produto: "VSAT Banda Ka",
    },
  ],
  en: [
    {
      id: "c-band",
      icon: "satellite",
      name: "C-band",
      subtitle: "4–8 GHz · Robust long-haul",
      featured: true,
      best_for:
        "Offshore oil & gas, broadcast TV, telecom backhaul and critical government operations in Angola.",
      tech: "3.7–4.2 GHz (downlink) and 5.9–6.4 GHz (uplink). Mature tech (1970s), GEO satellites. Moderate throughput (Mbps), latency ~600 ms.",
      antenna: "1.8 m to 3.7 m (rural up to 4.5 m)",
      rain: "excellent",
      pros: [
        "Virtually immune to heavy rain (tropical)",
        "Wide-beam coverage — high availability",
        "Mature, well-supported technology",
        "Ideal for Angola given the rainfall pattern",
      ],
      cons: [
        "Large antennas (1.8–3.7 m) — require foundation",
        "More expensive modems and BUC/LNB",
        "Lower throughput than Ku/Ka",
      ],
      produto: "VSAT C-band",
    },
    {
      id: "ku-band",
      icon: "radio",
      name: "Ku-band",
      subtitle: "12–18 GHz · Cost-performance balance",
      featured: false,
      best_for:
        "Companies, NGOs, province offices, mining sites and ops that need decent throughput at medium cost.",
      tech: "11.7–12.2 GHz (downlink) and 14–14.5 GHz (uplink). Throughput 2–50+ Mbps, latency ~600 ms. Industry standard.",
      antenna: "0.74 m to 1.8 m",
      rain: "moderate",
      pros: [
        "Compact antennas — easy install",
        "Best cost-throughput balance",
        "Vast hub & modem ecosystem (iDirect/Newtec)",
        "Native TCP acceleration and VPN",
      ],
      cons: [
        "Heavy-rain attenuation (occasional rain fade)",
        "Careful link budget needed on the Angolan coast",
        "Throughput limited by contention ratio",
      ],
      produto: "VSAT Ku-band",
    },
    {
      id: "ka-band",
      icon: "zap",
      name: "Ka-band",
      subtitle: "18–40 GHz · High-throughput (HTS)",
      featured: false,
      best_for:
        "High-speed internet for small sites, SOHO, IoT, mobile connectivity and fiber backup.",
      tech: "18.3–20.2 GHz (downlink) and 27.5–30 GHz (uplink). HTS satellites with spot beams — rates up to 100+ Mbps per terminal.",
      antenna: "0.74 m to 1.2 m (compact)",
      rain: "sensitive",
      pros: [
        "Highest available throughput (100+ Mbps)",
        "Lower Mbit/€ cost",
        "Small, light antennas",
        "Ideal for fast SOHO internet",
      ],
      cons: [
        "Very sensitive to tropical rain (strong rain fade)",
        "GEO latency identical to Ku (~600 ms)",
        "Coverage limited to the HTS satellite footprint",
        "Less suited to mission-critical workloads",
      ],
      produto: "VSAT Ka-band",
    },
  ],
  es: [
    {
      id: "c-band",
      icon: "satellite",
      name: "Banda C",
      subtitle: "4–8 GHz · Long-haul robusta",
      featured: true,
      best_for:
        "Petróleo offshore, broadcast TV, backhaul telecom y operaciones gubernamentales críticas en Angola.",
      tech: "3.7–4.2 GHz (downlink) y 5.9–6.4 GHz (uplink). Tecnología madura (años 70), satélites GEO. Throughput moderado (Mbps), latencia ~600 ms.",
      antenna: "1,8 m a 3,7 m (rural hasta 4,5 m)",
      rain: "excellent",
      pros: [
        "Prácticamente inmune a lluvia tropical fuerte",
        "Cobertura wide-beam — alta disponibilidad",
        "Tecnología estable y bien soportada",
        "Ideal para Angola por el patrón de lluvias",
      ],
      cons: [
        "Antenas grandes (1,8–3,7 m) — requieren cimentación",
        "Modems y BUC/LNB más caros",
        "Throughput menor que Ku/Ka",
      ],
      produto: "VSAT Banda C",
    },
    {
      id: "ku-band",
      icon: "radio",
      name: "Banda Ku",
      subtitle: "12–18 GHz · Equilibrio coste/rendimiento",
      featured: false,
      best_for:
        "Empresas, ONGs, oficinas en provincias, sitios mineros y operaciones con buen throughput a coste medio.",
      tech: "11.7–12.2 GHz (downlink) y 14–14.5 GHz (uplink). Throughput 2–50+ Mbps, latencia ~600 ms. Estándar de la industria.",
      antenna: "0,74 m a 1,8 m",
      rain: "moderate",
      pros: [
        "Antenas compactas — fácil instalación",
        "Mejor equilibrio coste/throughput",
        "Amplio ecosistema de hubs/modems iDirect/Newtec",
        "Aceleración TCP y VPN nativa",
      ],
      cons: [
        "Atenuación con lluvia fuerte (rain fade ocasional)",
        "Requiere link budget cuidadoso en la costa angoleña",
        "Throughput limitado por contention ratio",
      ],
      produto: "VSAT Banda Ku",
    },
    {
      id: "ka-band",
      icon: "zap",
      name: "Banda Ka",
      subtitle: "18–40 GHz · HTS de alta velocidad",
      featured: false,
      best_for:
        "Internet rápida en sitios pequeños, SOHO, IoT, conectividad móvil y respaldo de fibra.",
      tech: "18.3–20.2 GHz (downlink) y 27.5–30 GHz (uplink). Satélites HTS con spot beams — hasta 100+ Mbps por terminal.",
      antenna: "0,74 m a 1,2 m (compacta)",
      rain: "sensitive",
      pros: [
        "Mayor throughput disponible (100+ Mbps)",
        "Coste Mbit/€ más bajo",
        "Antenas pequeñas y ligeras",
        "Ideal para internet rápida en SOHO",
      ],
      cons: [
        "Muy sensible a lluvia tropical (rain fade fuerte)",
        "Latencia GEO igual a Ku (~600 ms)",
        "Cobertura limitada al footprint del HTS",
        "Menos adecuada para misiones críticas",
      ],
      produto: "VSAT Banda Ka",
    },
  ],
};

const ICON_MAP = {
  satellite: Satellite,
  radio: Radio,
  zap: Zap,
};

// Tailwind class tokens for the rain-tolerance chip, by severity level.
// Keeping these as a lookup map avoids nested ternaries inside JSX and
// makes it trivial to add a new severity (e.g. "blocked") in the future.
const RAIN_STYLES = {
  excellent: {
    box: "border-emerald-400/30 bg-emerald-500/10",
    label: "text-emerald-200",
    value: "text-emerald-100",
  },
  moderate: {
    box: "border-amber-400/30 bg-amber-500/10",
    label: "text-amber-200",
    value: "text-amber-100",
  },
  sensitive: {
    box: "border-rose-400/30 bg-rose-500/10",
    label: "text-rose-200",
    value: "text-rose-100",
  },
};

const BRAND_LABEL = "VSAT";

export const VsatBandCompare = () => {
  const { lang } = useLang();
  const navigate = useNavigate();
  const copy = COPY[lang] || COPY.pt;
  const bands = BANDS[lang] || BANDS.pt;

  const requestQuote = (produto) => {
    const params = new URLSearchParams({ produto, marca: BRAND_LABEL });
    navigate(`/?${params.toString()}#contact`);
  };

  return (
    <section
      id="vsat-compare"
      data-testid="vsat-compare"
      className="relative scroll-mt-24 overflow-hidden border-t border-slate-200 bg-gradient-to-b from-[#0b1d3a] via-[#0a1730] to-[#0b1d3a] py-20 text-white lg:py-24"
    >
      <div
        aria-hidden
        className="pointer-events-none absolute -top-40 right-1/4 h-[500px] w-[500px] rounded-full bg-indigo-500/15 blur-3xl"
      />
      <div
        aria-hidden
        className="pointer-events-none absolute -bottom-32 left-1/4 h-96 w-96 rounded-full bg-amber-500/10 blur-3xl"
      />

      <div className="relative mx-auto max-w-[1400px] px-6 md:px-12 lg:px-16">
        {/* Header */}
        <div className="max-w-3xl">
          <div className="flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.28em] text-amber-300">
            <Satellite className="h-3.5 w-3.5" strokeWidth={2.5} />
            {copy.overline}
          </div>
          <h2
            data-testid="vsat-compare-title"
            className="font-heading mt-4 text-3xl font-black tracking-tight text-white md:text-4xl lg:text-5xl"
          >
            {copy.title}
          </h2>
          <p className="mt-4 text-sm leading-relaxed text-slate-300 md:text-base">
            {copy.subtitle}
          </p>
        </div>

        {/* 3-card grid */}
        <div className="mt-12 grid grid-cols-1 gap-5 md:grid-cols-3">
          {bands.map((b) => {
            const Icon = ICON_MAP[b.icon] || Satellite;
            return (
              <article
                key={b.id}
                data-testid={`vsat-compare-${b.id}`}
                className={`group relative flex flex-col rounded-3xl border bg-slate-900/60 p-6 shadow-xl backdrop-blur-sm transition-all hover:-translate-y-1 hover:shadow-2xl ${
                  b.featured
                    ? "border-amber-400/50 ring-2 ring-amber-400/40"
                    : "border-white/10"
                }`}
              >
                {b.featured && (
                  <div className="absolute -top-3 left-6 inline-flex items-center gap-1 rounded-full bg-amber-400 px-2.5 py-1 text-[10px] font-bold uppercase tracking-[0.18em] text-slate-900 shadow-lg">
                    <Sparkles className="h-3 w-3" strokeWidth={2.5} />
                    {copy.labels.pick}
                  </div>
                )}

                <div className="flex items-center gap-3">
                  <div
                    className={`inline-flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl ${
                      b.featured
                        ? "bg-amber-500/20 text-amber-300"
                        : "bg-indigo-500/20 text-indigo-300"
                    }`}
                  >
                    <Icon className="h-6 w-6" strokeWidth={2.2} />
                  </div>
                  <div>
                    <div className="font-heading text-lg font-bold tracking-tight text-white">
                      {b.name}
                    </div>
                    <div className="text-[11px] font-medium uppercase tracking-wider text-slate-400">
                      {b.subtitle}
                    </div>
                  </div>
                </div>

                {/* Best for */}
                <div className="mt-5">
                  <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-amber-300/80">
                    {copy.labels.best_for}
                  </div>
                  <p className="mt-1.5 text-sm leading-relaxed text-slate-200">
                    {b.best_for}
                  </p>
                </div>

                {/* Technical box */}
                <div className="mt-4 rounded-xl border border-white/10 bg-white/5 p-3">
                  <div className="flex items-center gap-1.5">
                    <Info
                      className="h-3 w-3 text-amber-300"
                      strokeWidth={2.5}
                    />
                    <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-amber-300/80">
                      {copy.labels.tech}
                    </span>
                  </div>
                  <p className="mt-1.5 text-[12.5px] leading-snug text-slate-300">
                    {b.tech}
                  </p>
                </div>

                {/* Antenna + Rain row */}
                <div className="mt-3 grid grid-cols-1 gap-2 sm:grid-cols-2">
                  <div className="rounded-lg border border-indigo-400/20 bg-indigo-500/10 p-2.5">
                    <div className="flex items-center gap-1 font-mono text-[9.5px] uppercase tracking-[0.18em] text-indigo-200">
                      <Satellite className="h-3 w-3" strokeWidth={2.5} />
                      {copy.labels.antenna}
                    </div>
                    <div className="mt-1 text-[12px] font-semibold leading-tight text-white">
                      {b.antenna}
                    </div>
                  </div>
                  <div
                    className={`rounded-lg border p-2.5 ${RAIN_STYLES[b.rain].box}`}
                  >
                    <div
                      className={`flex items-center gap-1 font-mono text-[9.5px] uppercase tracking-[0.18em] ${RAIN_STYLES[b.rain].label}`}
                    >
                      <CloudRain className="h-3 w-3" strokeWidth={2.5} />
                      {copy.labels.rain}
                    </div>
                    <div
                      className={`mt-1 text-[11px] font-semibold leading-tight ${RAIN_STYLES[b.rain].value}`}
                    >
                      {copy.rain_levels[b.rain]}
                    </div>
                  </div>
                </div>

                {/* Pros */}
                <div className="mt-4 flex-1">
                  <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-emerald-300/80">
                    {copy.labels.pros}
                  </div>
                  <ul className="mt-2 space-y-1.5">
                    {b.pros.map((p) => (
                      <li
                        key={p}
                        className="flex items-start gap-1.5 text-[12.5px] leading-snug text-slate-200"
                      >
                        <CheckCircle2
                          className="mt-0.5 h-3.5 w-3.5 shrink-0 text-emerald-400"
                          strokeWidth={2.5}
                        />
                        <span>{p}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                {/* Cons */}
                <div className="mt-3">
                  <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-rose-300/80">
                    {copy.labels.cons}
                  </div>
                  <ul className="mt-2 space-y-1">
                    {b.cons.map((p) => (
                      <li
                        key={p}
                        className="flex items-start gap-1.5 text-[12px] leading-snug text-slate-400"
                      >
                        <span
                          aria-hidden
                          className="mt-1.5 inline-block h-1 w-1 shrink-0 rounded-full bg-rose-400"
                        />
                        <span>{p}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                <button
                  type="button"
                  onClick={() => requestQuote(b.produto)}
                  data-testid={`vsat-compare-cta-${b.id}`}
                  className={`mt-5 inline-flex w-full items-center justify-center gap-1.5 rounded-full px-4 py-2.5 text-sm font-bold shadow-md transition-all hover:-translate-y-0.5 hover:shadow-lg ${
                    b.featured
                      ? "bg-amber-400 text-slate-900 hover:bg-amber-300"
                      : "bg-white text-slate-900 hover:bg-slate-100"
                  }`}
                >
                  {copy.labels.cta}
                  <ArrowRight className="h-4 w-4" strokeWidth={2.5} />
                </button>
              </article>
            );
          })}
        </div>

        {/* Help CTA */}
        <div className="mt-12 rounded-3xl border border-amber-400/30 bg-gradient-to-br from-[#0b1d3a] via-amber-950/20 to-[#0b1d3a] p-6 md:p-8">
          <div className="flex flex-col items-start gap-4 md:flex-row md:items-center md:justify-between">
            <div className="max-w-2xl">
              <h3 className="font-heading text-lg font-bold tracking-tight text-white md:text-xl">
                {copy.cta_help.title}
              </h3>
              <p className="mt-1.5 text-sm leading-relaxed text-slate-300">
                {copy.cta_help.body}
              </p>
            </div>
            <a
              href="/#contact"
              data-testid="vsat-compare-help-cta"
              className="inline-flex shrink-0 items-center gap-2 rounded-full bg-amber-400 px-6 py-3 text-sm font-bold text-slate-900 shadow-md transition-all hover:-translate-y-0.5 hover:bg-amber-300 hover:shadow-lg"
            >
              {copy.cta_help.btn}
              <ArrowRight className="h-4 w-4" strokeWidth={2.5} />
            </a>
          </div>
        </div>
      </div>
    </section>
  );
};

export default VsatBandCompare;
